/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Button,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import {
  deleteContactoAcreedor,
  getContactoAcreedor,
  getContactosAcreedores,
  patchContactoAcreedor,
  postContactoAcreedor,
} from 'api/configuraciones/contactosAcreedores';
import { getEstadosGlobales, getLadas } from 'api/catalogos';
import LadaNumero from 'components/LadaNumero';
import { getModelPermission } from 'utils/permission';
import ModalDelete from 'components/ModalDelete';
import { useSelector } from 'react-redux';

export const acreedorContactoPermission = {
  permissionModel: 'contactodeacreedor',
  catalogsModels: [],
  tabsModels: [],
};

const AcreedoresContactos = ({
  acreedorId,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ladas, setLadas] = useState([]);
  const [estadosGlobales, setestadosGlobales] = useState([]);
  const [data, setData] = useState([]);
  const ladaDefaultID = useSelector(({ auth }) => auth.entidad?.configuracion?.lada);

  const getPermission = getModelPermission(
    acreedorContactoPermission.permissionModel,
    acreedorContactoPermission.catalogsModels,
    acreedorContactoPermission.tabsModels,
    false,
  );

  const fetchData = async () => {
    setLoading(true);
    const _data = await getContactosAcreedores({ acreedor: acreedorId });
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [
        _estadosGlobales,
        _ladas,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getLadas(),
        getContactosAcreedores({ acreedor: acreedorId }),
      ]);
      if (mounted) {
        setestadosGlobales(_estadosGlobales);
        setLadas(_ladas);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setVisible(false);
    form.resetFields();
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(false);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _contactoAcreedor = await getContactoAcreedor(match.id);
    setTimeout(() => {
      if (_contactoAcreedor) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue(formatReceived(_contactoAcreedor));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      // Si no se selecciona una lada se envía la configurada en la entidad
      if (!values.lada) {
        values.lada = ladaDefaultID;
      }
      const [key] = selectedRowKeys;
      // eslint-disable-next-line max-len
      const saved = await (key ? patchContactoAcreedor(key, { ...values, acreedor: acreedorId }) : postContactoAcreedor({ ...values, acreedor: acreedorId }));
      if (saved) {
        if (_continue) {
          await fetchData();
          setSelectedRowKeys([saved.id]);
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteContactoAcreedor(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 200,
      render: (_, values) => `${values?.nombre} ${values?.apellido_paterno} ${values?.apellido_materno || ''}`,
    },
    {
      titleText: 'Correo Electrónico',
      dataIndex: 'correo_electronico',
      key: 'correo_electronico',
      width: 200,
    },
    {
      titleText: 'Número de Teléfono',
      dataIndex: 'numero_de_telefono',
      key: 'numero_de_telefono',
      width: 200,
      render: (_, values) => {
        const _lada = ladas.find((e) => e.id === values?.lada)?.lada;
        return `${_lada} ${values?.numero_de_telefono}`;
      },
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto',
      key: 'puesto',
      width: 200,
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 400,
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 200,
      render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [
      {
        required: true,
        message: 'El campo es requerido',
      },
    ],
    correo_electronico: [
      requiredRule,
      {
        type: 'email',
        message: 'Ingrese un correo electrónico válido',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={getPermission}
          allowExportPDF={false}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Contacto`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          width="50%"
          onCancel={onCancel}
          forceRender
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            name="general"
            initialValues={{ estados_globales: 1 }}
          >
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      name="nombre"
                      label="Nombre(s)"
                      rules={rules.required}
                    >
                      <Input
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="apellido_paterno"
                      label="Apellido Paterno"
                      rules={rules.required}
                    >
                      <Input
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="apellido_materno"
                      label="Apellido Materno"
                    >
                      <Input
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="estados_globales"
                      label="Estado"
                      rules={rules.estado}
                    >
                      <Select
                        disabled={selectedRowKeys.length <= 0}
                        dataSource={estadosGlobales}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Row>
                  <Col span={24}>
                    <Form.Item
                      name="correo_electronico"
                      label="Correo Electrónico"
                      rules={rules.correo_electronico}
                    >
                      <Input allowClear />
                    </Form.Item>
                  </Col>
                  <LadaNumero
                    form={form}
                    fullSize
                    required
                  />
                  <Col span={24}>
                    <Form.Item
                      name="puesto"
                      label="Puesto"
                      rules={rules.required}
                      hasFeedback
                    >
                      <Input
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="observaciones"
                  label="Observaciones"
                  hasFeedback
                >
                  <Input.TextArea
                    autoSize={{ minRows: 2, maxRows: 3 }}
                    maxLength={250}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`El Contacto ${form.getFieldValue('nombre')} ${form.getFieldValue('apellido_paterno')} ${form.getFieldValue('apellido_materno') || ''}`}
        />
      </Spin>
    </Row>
  );
};

export default AcreedoresContactos;
